import React, {useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useQueryClient} from "@tanstack/react-query";
import {useB2BAgencies, B2B_AGENCIES_QUERY_KEY} from "../../hooks/useB2BAgencies.ts";
import {B2BAgencyItem} from "../../domain/entities/B2BAgency.ts";
import {CreateUpdateAgencyDialog} from "../dialogs/CreateUpdateAgencyDialog.tsx";
import {FilterDropdown} from "./FilterDropdown.tsx";
import {b2bMarketFlag, b2bMarketLabel} from "../../utils/b2bMarketDisplay.ts";
import {initialsOf} from "../../utils/initials.ts";
import {getAgencyAssetUrl} from "../../utils/AssetsCDN.ts";
import {formatNumber} from "../../utils/formatNumber.ts";
import IconSearch from '../../assets/icons/search.svg?react';
import IconPlus from '../../assets/icons/plus.svg?react';
import IconChevronDown from '../../assets/icons/chevron-down.svg?react';
import IconBook from '../../assets/icons/book.svg?react';

const PAGE_SIZE = 25

// useGrouping must be forced 'always': es-ES's 'auto' default uses "min2"
// grouping, which drops the thousands separator for 4-digit values (e.g. 7829).
const formatMoney = (value: number, currency: string) =>
    value.toLocaleString('es-ES', {style: 'currency', currency, maximumFractionDigits: 0, useGrouping: 'always'} as any)

const normalize = (value: string) => value.trim().normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase()

type AgenciesTableProps = {
    setPath: (path: string) => void
}

export const AgenciesTable: React.FC<AgenciesTableProps> = ({setPath}) => {
    const {t} = useTranslation('dashboard')
    const {agencies, facets, isLoadingAgencies} = useB2BAgencies()
    const queryClient = useQueryClient()
    const createEntityDialog = useRef<React.ElementRef<typeof CreateUpdateAgencyDialog>>(null)

    const [searchInput, setSearchInput] = useState('')
    const [marketFilter, setMarketFilter] = useState<string[]>([])
    const [amFilter, setAmFilter] = useState<string[]>([])
    const [page, setPage] = useState(0)

    const normalizedSearch = normalize(searchInput)

    const filteredRows = useMemo(() => {
        return agencies
            .filter((agency) => {
                if (normalizedSearch && !normalize(agency.name).includes(normalizedSearch)) return false
                if (marketFilter.length > 0 && !marketFilter.includes(agency.market)) return false
                if (amFilter.length > 0 && (!agency.accountManager || !amFilter.includes(agency.accountManager))) return false
                return true
            })
            .sort((a, b) => b.ltv - a.ltv)
    }, [agencies, normalizedSearch, marketFilter, amFilter])

    useEffect(() => setPage(0), [normalizedSearch, marketFilter, amFilter])

    const hasActiveFilters = !!normalizedSearch || marketFilter.length > 0 || amFilter.length > 0
    const pageCount = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE))
    const currentPage = Math.min(page, pageCount - 1)
    const pageRows = filteredRows.slice(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE)

    const clearFilters = () => {
        setSearchInput('')
        setMarketFilter([])
        setAmFilter([])
    }

    return (
        <div className="flex flex-col bg-neutralBlack-100 w-full h-full overflow-hidden">
            {/* Header */}
            <div className="flex items-center gap-4 flex-wrap mb-1">
                <div className="flex items-baseline gap-2.5 min-w-0">
                    <h1 className="text-[26px] font-semibold tracking-tight text-neutralBlack-900">{t('agencies')}</h1>
                    <span className="text-base font-medium text-neutralBlack-500">{formatNumber(agencies.length)}</span>
                </div>
                <span className="flex-1 min-w-[24px]"/>
                <label className="flex items-center gap-2 bg-white border border-neutralBlack-200 rounded-xl px-3.5 h-10 w-[260px] max-w-[40vw] shadow-sm">
                    <IconSearch className="w-4 h-4 flex-none text-neutralBlack-500"/>
                    <input
                        type="text"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder={t('searchYourAgency')}
                        className="flex-1 min-w-0 border-0 outline-none bg-transparent text-sm text-neutralBlack-900"
                    />
                </label>
                <button
                    type="button"
                    onClick={() => createEntityDialog.current!.open()}
                    className="flex items-center gap-1.5 h-10 px-[18px] rounded-full bg-neutralBlack-900 text-white text-sm font-medium hover:bg-neutralBlack-800"
                >
                    <IconPlus className="w-3 h-3"/>
                    {t('addNewAgency')}
                </button>
            </div>

            {/* Filter bar */}
            <div className="flex items-center gap-2 flex-wrap mb-4 mt-3">
                <FilterDropdown
                    label={t('agenciesTable.marketFilter')}
                    options={(facets?.markets ?? []).map((m) => ({value: m, label: b2bMarketLabel(m)}))}
                    selected={marketFilter}
                    onChange={setMarketFilter}
                />
                {!!facets?.accountManagers?.length && (
                    <FilterDropdown
                        label={t('agenciesTable.accountManagerFilter')}
                        options={facets.accountManagers.map((am) => ({value: am, label: am}))}
                        selected={amFilter}
                        onChange={setAmFilter}
                    />
                )}
            </div>

            {/* Table / states */}
            <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar">
                <div className="bg-white border border-neutralBlack-200 rounded-2xl shadow-sm overflow-hidden">

                    {isLoadingAgencies ? (
                        <div>
                            {Array.from({length: 8}).map((_, i) => (
                                <div key={i} className="flex items-center gap-6 px-5 py-[15px] border-b border-neutralBlack-100 animate-pulse">
                                    <span className="w-[34px] h-[34px] flex-none rounded-[10px] bg-neutralBlack-100"/>
                                    <span className="flex flex-col gap-1.5 w-[180px]">
                                        <span className="w-[140px] h-2.5 rounded-full bg-neutralBlack-100"/>
                                        <span className="w-[96px] h-2 rounded-full bg-neutralBlack-50"/>
                                    </span>
                                    <span className="w-12 h-2.5 rounded-full bg-neutralBlack-100"/>
                                    <span className="w-[110px] h-2.5 rounded-full bg-neutralBlack-100"/>
                                    <span className="flex-1"/>
                                    <span className="w-20 h-2.5 rounded-full bg-neutralBlack-100"/>
                                </div>
                            ))}
                        </div>
                    ) : filteredRows.length === 0 ? (
                        <div className="flex flex-col items-center text-center py-[72px] px-8">
                            <span className="w-24 h-24 rounded-[20px] bg-neutralBlack-50 border border-dashed border-neutralBlack-300 flex items-center justify-center mb-6">
                                <IconBook className="w-8 h-8 text-neutralBlack-400"/>
                            </span>
                            <h2 className="text-[19px] font-semibold tracking-tight text-neutralBlack-900 mb-2">
                                {hasActiveFilters ? t('agenciesTable.noResultsWithFilters') : t('noAgenciesYet')}
                            </h2>
                            <p className="max-w-[380px] text-sm leading-relaxed text-neutralBlack-500 mb-6">
                                {hasActiveFilters
                                    ? t('agenciesTable.tryRemovingFilter')
                                    : t('agenciesTable.createFirstAgency')}
                            </p>
                            <button
                                type="button"
                                onClick={() => hasActiveFilters ? clearFilters() : createEntityDialog.current!.open()}
                                className="h-10 px-[18px] rounded-full bg-neutralBlack-900 text-white text-sm font-medium hover:bg-neutralBlack-800"
                            >
                                {hasActiveFilters ? t('agenciesTable.clearFilters') : `+ ${t('addNewAgency')}`}
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="w-full min-w-[1080px] border-collapse text-sm">
                                    <thead>
                                        <tr>
                                            <Th align="left">Agencia</Th>
                                            <Th align="left">Mercado</Th>
                                            <Th align="left">Account Manager</Th>
                                            <Th align="right">Packs</Th>
                                            <Th align="right">TripBooks</Th>
                                            <Th align="left">Printing %</Th>
                                            <Th align="right">LTV</Th>
                                            <Th align="left">Renovación</Th>
                                            <th className="w-[52px] border-b border-neutralBlack-100"/>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pageRows.map((agency) => (
                                            <AgencyRow key={agency.id} agency={agency} onOpen={() => setPath('agency/' + agency.id)}/>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="flex items-center gap-3 flex-wrap px-5 py-3.5">
                                <span className="text-[13px] text-neutralBlack-500">
                                    {formatNumber(currentPage * PAGE_SIZE + 1)}–{formatNumber(Math.min(filteredRows.length, (currentPage + 1) * PAGE_SIZE))} de {formatNumber(filteredRows.length)} · {PAGE_SIZE} por página
                                </span>
                                <span className="flex-1 min-w-[12px]"/>
                                <button
                                    type="button"
                                    disabled={currentPage === 0}
                                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                                    className="w-[30px] h-[30px] flex items-center justify-center rounded-full border border-neutralBlack-200 bg-white text-neutralBlack-800 disabled:text-neutralBlack-300 disabled:cursor-not-allowed hover:bg-neutralBlack-50 disabled:hover:bg-white"
                                >
                                    <IconChevronDown className="w-3 h-3 rotate-90"/>
                                </button>
                                <button
                                    type="button"
                                    disabled={currentPage >= pageCount - 1}
                                    onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                                    className="w-[30px] h-[30px] flex items-center justify-center rounded-full border border-neutralBlack-200 bg-white text-neutralBlack-800 disabled:text-neutralBlack-300 disabled:cursor-not-allowed hover:bg-neutralBlack-50 disabled:hover:bg-white"
                                >
                                    <IconChevronDown className="w-3 h-3 -rotate-90"/>
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <CreateUpdateAgencyDialog
                ref={createEntityDialog}
                title={t('addNewAgency')}
                onComplete={() => queryClient.invalidateQueries({queryKey: B2B_AGENCIES_QUERY_KEY})}
            />
        </div>
    )
}

const AgencyRow: React.FC<{agency: B2BAgencyItem; onOpen: () => void}> = ({agency, onOpen}) => {
    const {t} = useTranslation('dashboard')
    const flag = b2bMarketFlag(agency.market)
    const printingPct = agency.tripbooks.printingPct != null ? Math.round(agency.tripbooks.printingPct * 100) : null

    return (
        <tr onClick={onOpen} className="cursor-pointer border-b border-neutralBlack-100 hover:bg-neutralBlack-50">
            <td className="px-5 py-3">
                <span className="flex items-center gap-3">
                    <span className="w-[34px] h-[34px] flex-none rounded-[10px] bg-neutralBlack-100 border border-neutralBlack-200 flex items-center justify-center overflow-hidden text-[11.5px] font-semibold text-neutralBlack-800">
                        {agency.logoName
                            ? <img src={getAgencyAssetUrl(agency.logoName)} alt={agency.name} className="w-full h-full object-contain p-1"/>
                            : initialsOf(agency.name)
                        }
                    </span>
                    <span className="flex flex-col gap-0.5 min-w-0">
                        <span className="text-sm font-medium text-neutralBlack-900 whitespace-nowrap">{agency.name}</span>
                        <span className="text-xs text-neutralBlack-500 whitespace-nowrap">{agency.subtitle ?? '—'}</span>
                    </span>
                </span>
            </td>
            <td className="px-4 py-3 text-neutralBlack-800 whitespace-nowrap">
                <span className="flex items-center gap-2">
                    {flag && <img src={flag} alt={agency.market} className="w-[18px] h-[13px] rounded-sm object-cover shadow-[0_0_0_1px_rgba(0,0,0,.08)]"/>}
                    {b2bMarketLabel(agency.market)}
                </span>
            </td>
            <td className="px-4 py-3 text-neutralBlack-800 whitespace-nowrap">{agency.accountManager ?? 'Sin asignar'}</td>
            <td className="px-4 py-3 text-right text-neutralBlack-800">{formatNumber(agency.packs)}</td>
            <td className="px-4 py-3 text-right whitespace-nowrap">
                <span className="text-neutralBlack-900 font-medium">{formatNumber(agency.tripbooks.printed)}</span>
                <span className="text-neutralBlack-400"> / </span>
                <span className="text-neutralBlack-800">{formatNumber(agency.tripbooks.purchased)}</span>
            </td>
            <td className="px-4 py-3">
                {printingPct != null ? (
                    <span className="flex items-center gap-2.5">
                        <span className="w-[72px] h-1 flex-none rounded-full bg-neutralBlack-200 overflow-hidden">
                            <span className="block h-1 rounded-full bg-neutralBlack-900" style={{width: `${Math.min(100, printingPct)}%`}}/>
                        </span>
                        <span className="text-[13px] text-neutralBlack-800 min-w-[34px]">{printingPct}%</span>
                    </span>
                ) : '—'}
            </td>
            <td className="px-4 py-3 text-right whitespace-nowrap text-neutralBlack-900 font-medium">{formatMoney(agency.ltv, agency.currency)}</td>
            <td className="px-4 py-3">
                {agency.renewal.due && (
                    <span className="inline-flex items-center gap-1.5 bg-neutralBlack-100 rounded-full pl-2 pr-2.5 py-1 text-xs text-neutralBlack-900 whitespace-nowrap">
                        <ClockIcon/>
                        {printingPct != null && printingPct >= 100 ? t('agenciesTable.packsExhausted') : `${t('agenciesTable.renewal')}${printingPct != null ? ` · ${printingPct}%` : ''}`}
                    </span>
                )}
            </td>
            <td className="px-4 py-3 text-right">
                <span
                    onClick={(e) => {e.stopPropagation(); onOpen()}}
                    className="w-7 h-7 inline-flex items-center justify-center rounded-full text-neutralBlack-400 hover:bg-neutralBlack-100 hover:text-neutralBlack-900"
                >
                    <KebabIcon/>
                </span>
            </td>
        </tr>
    )
}

const Th: React.FC<{children: React.ReactNode; align: 'left' | 'right'}> = ({children, align}) => (
    <th className={`px-4 py-3.5 text-[11.5px] font-medium tracking-wide uppercase text-neutralBlack-500 border-b border-neutralBlack-100 whitespace-nowrap ${align === 'right' ? 'text-right' : 'text-left'}`}>
        {children}
    </th>
)

const KebabIcon = () => (
    <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
        <circle cx="4" cy="9" r="1.2" fill="currentColor"/>
        <circle cx="9" cy="9" r="1.2" fill="currentColor"/>
        <circle cx="14" cy="9" r="1.2" fill="currentColor"/>
    </svg>
)

const ClockIcon = () => (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
        <circle cx="7" cy="7" r="5.3" stroke="currentColor" strokeWidth="1.3"/>
        <path d="M7 4.3V7l2 1.3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
    </svg>
)
